import { useEffect, useState } from "react";
import { Tag } from "lucide-react";
import { api } from "@/lib/api";

const PALETTE = [
    "#F59E0B", "#3B82F6", "#10B981", "#EC4899", "#A855F7",
    "#06B6D4", "#F97316", "#84CC16", "#EF4444", "#8B5CF6",
];
function themeColor(t) {
    if (!t) return "#71717A";
    let h = 0;
    for (let i = 0; i < t.length; i++) h = (h * 31 + t.charCodeAt(i)) >>> 0;
    return PALETTE[h % PALETTE.length];
}

const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
    return `$${Number(n).toFixed(0)}`;
};

/**
 * Dashboard widget — which strategic themes are actually monetizing in your pipeline?
 * Reads from GET /api/dashboard/themes.
 */
export default function ThemeStatsWidget() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/dashboard/themes").then(({ data }) => setData(data)).catch(() => setData({ rows: [], total_themes: 0 }));
    }, []);

    if (!data) return null;
    if (!data.rows || data.rows.length === 0) {
        return null; // Hide until the user has saved synergies on themed firms
    }

    const maxValue = Math.max(...data.rows.map((r) => r.total_value));

    return (
        <div className="border border-zinc-800 bg-zinc-950 mb-4" data-testid="theme-stats-widget">
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-amber-500" />
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500">Pipeline by theme</div>
                </div>
                <div className="font-mono text-[10px] text-zinc-500">Which strategic clusters are monetizing?</div>
            </div>
            <div className="p-5 space-y-2.5">
                {data.rows.map((r) => (
                    <div key={r.theme} className="flex items-center gap-3" data-testid={`theme-row-${r.theme}`}>
                        <div className="w-32 shrink-0 flex items-center gap-2">
                            <span className="w-2.5 h-2.5 inline-block rounded-full shrink-0" style={{ background: themeColor(r.theme) }} />
                            <span className="font-mono text-xs text-zinc-200 truncate">{r.theme}</span>
                        </div>
                        <div className="flex-1 h-5 bg-zinc-900 relative">
                            <div
                                className="absolute inset-y-0 left-0"
                                style={{ width: `${(r.total_value / maxValue) * 100}%`, background: themeColor(r.theme), opacity: 0.65 }}
                            />
                            <div className="absolute inset-0 flex items-center justify-end px-2 font-mono text-[10px] text-zinc-50">
                                {fmtMoney(r.total_value)}
                            </div>
                        </div>
                        <div className="w-32 shrink-0 text-right font-mono text-[10px] text-zinc-500">
                            <span className="text-zinc-200 font-bold">{r.synergy_count}</span> synergies · fit <span className="text-amber-400">{r.avg_fit}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
